import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { useQuotePartActions } from 'quotes/hooks';
import { mapQuotePartsMoveTo } from 'quotes/mappers';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { IMoveToForm, IQuotePart } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';

import QuotePartMoveToFormView from './QuotePartMoveToFormView';
import { quotePartMoveToSchema } from './schema';

interface IProps {
  isOpen?: boolean;
  onClose?: (closeSelection?: boolean) => void;
  quoteId: number;
  onSubmitSuccess?: () => void;
}

const QuotePartMoveToForm = ({ isOpen = false, onClose, quoteId, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { showSuccessNotification } = useNotificationContext();
  const { rows, gridApi, selectedRows } = useAgGridClientTableContext();
  const { currentQuoteVersion } = useCustomerQuotePartsContext();

  const { handleCliJob } = useCliJobHandler();
  const { moveTo } = useQuotePartActions();

  const activeRowData = getActiveQuoteParts(rows as IQuotePart[]);
  const maxLineNumber = activeRowData[activeRowData.length - 1]?.line || 0;

  const onSubmit = (reset: UseFormReset<IMoveToForm>) => async (data: IMoveToForm) => {
    handleCliJob(moveTo, mapQuotePartsMoveTo(quoteId, currentQuoteVersion, selectedRows, data), {
      cliJobInProgressMessage: t('messageLinesMoveToInProgress', {
        count: selectedRows.length,
      }),
      onCliJobFinished: () => {
        showSuccessNotification(
          {
            message: t('messageLinesMoveToSuccess', { count: selectedRows.length }),
          },
          NotificationPosition.FORM
        );

        onSubmitSuccess && onSubmitSuccess();

        handleOnClose(reset, true);

        gridApi?.setGridOption('loading', true);
        queryClient.invalidateQueries('customerQuoteParts');
      },
      notificationPosition: NotificationPosition.FORM,
    });
  };

  const handleOnClose = (reset: UseFormReset<IMoveToForm>, closeSelection = false) => {
    reset({ lineNumber: null });
    onClose(closeSelection);
  };

  return (
    <FormProvider schema={quotePartMoveToSchema(maxLineNumber)}>
      <QuotePartMoveToFormView
        isOpen={isOpen}
        maxLineNumber={maxLineNumber}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};

export default QuotePartMoveToForm;
